/**
 * ConceptMapView — la "Mappa dei concetti" in DUE modalità complementari,
 * osservabili attraverso DUE lenti (concetto / problema).
 *
 * Port adattato del golden ThemeMapView (Letteratura inglese) alla Filosofia.
 * Dove il golden ragiona su "temi" (movimenti / generi), qui ragioniamo su
 * CONCETTI (21) raggruppati sotto i 7 PROBLEMI FONDAMENTALI ministeriali. Il
 * rail in alto fa da indice/selettore e ospita il toggle della LENTE:
 *
 *   · lente CONCETTO (default) — rail dei 21 concetti, raggruppati per problema
 *     (barra-colore = tinta del problema-genitore); i nodi sono le TAPPE del
 *     concetto scelto (un filosofo per tappa).
 *   · lente PROBLEMA — rail dei 7 problemi fondamentali; i nodi sono gli AUTORI
 *     che toccano quel problema (una tappa rappresentativa per autore).
 *
 * Due modalità (come nel golden):
 *
 *   ★ "Nel tempo" (default) — il concetto/problema è un FILO che attraversa i
 *      secoli: i filosofi-sfera su UNA riga cronologica per anno di nascita,
 *      spaziatura in PIXEL REALI (MIN_GAP) con scroll orizzontale se non entrano
 *      (mai schiacciati). Fasce di secolo (a.C./d.C.), pavimento + ombra di
 *      contatto, filo materico a 3 tratti, archi di trasmissione (relazioni di
 *      influenza) SENZA marker-freccia, e "Racconta" che accende i nodi
 *      sinistra→destra col map_blurb della tappa.
 *
 *   ● "Confronto" — concetto/problema al centro (hub-pill), filosofi in cerchio
 *      = selettore; 2-3 scelti → colonne affiancate con la spiegazione della
 *      tappa (+ eventuale citazione) e "apri tappa".
 *
 * Click su un nodo → navigate:tappa { id, conceptId? } (deep-link alla tappa di
 * quel filosofo su quel concetto). Tutto front-end: legge i dati statici via
 * getDataService(); nessun backend.
 */

import { BaseComponent } from '@/core/BaseComponent';
import type { ComponentContext } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import type {
  ConceptLocalized,
  PhilosopherLocalized,
  ProblemaFondamentale,
  TappaLocalized,
} from '@/data/schema';
import { PROBLEMI_FONDAMENTALI } from '@/data/schema';
import { CATEGORY_COLORS } from '@/data/category-colors';
import { philosopherSphereColor, authorSphere, authorSphereDefs } from './author-sphere';
import { computeDisplayName } from './graph-layout';
import { problemaLabel, formatYear } from './format';
import { fitText, type FitTextHandle } from '@/core/fit-text';

type Mode = 'time' | 'compare';
type Lens = 'concept' | 'problem';

/** Stato serializzabile → il router lo cattura prima di aprire una tappa e lo
 *  ripristina al "← Indietro", così non si perde il filo. */
export interface ConceptMapState {
  mode: Mode;
  lens: Lens;
  selectedId: string | null; // concept id (lente concetto) o problema (lente problema)
  compare: string[];         // autori selezionati per il confronto
  narrOn: boolean;
  narrI: number;
}

/** Nodo posizionato sulla scena: un filosofo + la sua tappa di riferimento. */
interface PlacedNode {
  author: PhilosopherLocalized;
  tappa: TappaLocalized;
  year: number; // anno di NASCITA (àncora cronologica sulla riga)
  x: number;
}

const MIN_GAP = 150; // distanza minima tra filosofi (oltre → scroll, mai appiccicati)
const NODE_R = 23;

export class ConceptMapView extends BaseComponent {
  private mode: Mode = 'time';
  private lens: Lens = 'concept';
  private selectedId: string | null = null;
  private narr = { on: false, i: 0 };
  private compare = new Set<string>();
  private resizeTimer?: number;
  private resizeObserver: ResizeObserver | null = null;
  /** Auto-fit del blurb "Racconta" nella caption ancorata (LIM): il testo si
   *  rimpicciolisce per entrare SEMPRE nella box, ovunque, senza scroll. */
  private captionFit: FitTextHandle | null = null;

  // Dati caricati una volta in onInit (il service è async; il render è sync).
  private allConcepts: ConceptLocalized[] = [];
  private authorById = new Map<string, PhilosopherLocalized>();
  private tappeByConcept = new Map<string, TappaLocalized[]>();
  private tappeByProblema = new Map<ProblemaFondamentale, TappaLocalized[]>();

  constructor(element: HTMLElement, context: ComponentContext, initial?: ConceptMapState) {
    super(element, context);
    if (initial) {
      this.mode = initial.mode;
      this.lens = initial.lens;
      this.selectedId = initial.selectedId;
      this.compare = new Set(initial.compare);
      this.narr = { on: initial.narrOn, i: initial.narrI };
    }
  }

  /** Snapshot dello stato corrente (consumato dal router per il back). */
  captureState(): ConceptMapState {
    return {
      mode: this.mode,
      lens: this.lens,
      selectedId: this.selectedId,
      compare: [...this.compare],
      narrOn: this.narr.on,
      narrI: this.narr.i,
    };
  }

  protected async onInit(): Promise<void> {
    this.element.classList.add('concept-map-view');

    const data = getDataService();
    const [authors, concepts, tappe] = await Promise.all([
      data.listAuthors({}, this.context.locale),
      data.listConcepts(this.context.locale),
      data.listTappe({}, this.context.locale),
    ]);
    this.allConcepts = concepts;
    this.authorById = new Map(authors.map((a) => [a.id, a]));

    for (const t of tappe) {
      const byC = this.tappeByConcept.get(t.concetto_id) ?? [];
      byC.push(t);
      this.tappeByConcept.set(t.concetto_id, byC);
      const byP = this.tappeByProblema.get(t.problema_fondamentale) ?? [];
      byP.push(t);
      this.tappeByProblema.set(t.problema_fondamentale, byP);
    }

    // Listener delegato UNICO: gli handler vivono qui, render() ridisegna l'HTML.
    this.addEventListener(this.element, 'click', (ev) => this.handleClick(ev));
    this.render();

    // Re-render su resize/rotazione (debounced): la timeline è calcolata in px
    // reali sulla larghezza disponibile, quindi va ricalcolata se cambia.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.render(), 200);
      });
      this.resizeObserver.observe(this.element);
      this.addCleanup(() => {
        this.resizeObserver?.disconnect();
        window.clearTimeout(this.resizeTimer);
      });
    }
    this.addCleanup(() => this.captionFit?.disconnect());
  }

  protected onLocaleChange(): void {
    // Ricarica i contenuti localizzati e ridisegna, preservando lo stato.
    void this.reloadAndRender();
  }

  private async reloadAndRender(): Promise<void> {
    const data = getDataService();
    const [authors, concepts, tappe] = await Promise.all([
      data.listAuthors({}, this.context.locale),
      data.listConcepts(this.context.locale),
      data.listTappe({}, this.context.locale),
    ]);
    this.allConcepts = concepts;
    this.authorById = new Map(authors.map((a) => [a.id, a]));
    this.tappeByConcept = new Map();
    this.tappeByProblema = new Map();
    for (const t of tappe) {
      const byC = this.tappeByConcept.get(t.concetto_id) ?? [];
      byC.push(t);
      this.tappeByConcept.set(t.concetto_id, byC);
      const byP = this.tappeByProblema.get(t.problema_fondamentale) ?? [];
      byP.push(t);
      this.tappeByProblema.set(t.problema_fondamentale, byP);
    }
    this.render();
  }

  // ───────────────────────── dati / selezione ─────────────────────────

  /** Problemi che hanno almeno una tappa, in ordine ministeriale canonico. */
  private presentProblemi(): ProblemaFondamentale[] {
    const list = PROBLEMI_FONDAMENTALI.filter((p) => (this.tappeByProblema.get(p)?.length ?? 0) > 0);
    return list.length > 0 ? [...list] : [...PROBLEMI_FONDAMENTALI];
  }

  /** Concetti con almeno una tappa, ordinati per problema-genitore + nome. */
  private presentConcepts(): ConceptLocalized[] {
    const order = new Map(PROBLEMI_FONDAMENTALI.map((p, i) => [p, i]));
    return this.allConcepts
      .filter((c) => (this.tappeByConcept.get(c.id)?.length ?? 0) > 0)
      .sort((a, b) => {
        const pa = order.get(a.problema_fondamentale_associato) ?? 99;
        const pb = order.get(b.problema_fondamentale_associato) ?? 99;
        if (pa !== pb) return pa - pb;
        return a.nome.localeCompare(b.nome);
      });
  }

  /** Concetto attivo (lente concetto). */
  private currentConcept(): ConceptLocalized | null {
    if (this.lens !== 'concept' || !this.selectedId) return null;
    return this.allConcepts.find((c) => c.id === this.selectedId) ?? null;
  }

  /** Problema attivo (lente problema). */
  private currentProblema(): ProblemaFondamentale | null {
    if (this.lens !== 'problem') return null;
    return (this.selectedId as ProblemaFondamentale) ?? null;
  }

  /** Etichetta del fuoco corrente (concetto o problema). */
  private focusLabel(): string {
    const c = this.currentConcept();
    if (c) return c.nome;
    const p = this.currentProblema();
    return p ? problemaLabel(p) : '';
  }

  /** Tinta di riferimento del fuoco corrente: teal per i concetti, ambra
   *  (declinata per il problema) per i problemi. */
  private accentColor(): string {
    if (this.lens === 'concept') return CATEGORY_COLORS.concept;
    const p = this.currentProblema();
    return p ? problemTint(p) : CATEGORY_COLORS.problem;
  }

  /** Nodi da posizionare, in ordine cronologico per anno di nascita. */
  private nodes(): PlacedNode[] {
    let placed: PlacedNode[] = [];
    if (this.lens === 'concept') {
      const cid = this.selectedId;
      if (!cid) return [];
      // Una tappa per (autore, concetto) → un nodo per tappa.
      for (const t of this.tappeByConcept.get(cid) ?? []) {
        const author = this.authorById.get(t.autore_id);
        if (author) placed.push({ author, tappa: t, year: author.anno_nascita, x: 0 });
      }
    } else {
      const p = this.currentProblema();
      if (!p) return [];
      // Un autore può avere più tappe sotto lo stesso problema → una sola sfera
      // per autore, con la tappa PIÙ ANTICA (anno_riferimento) come rappresentante.
      const rep = new Map<string, TappaLocalized>();
      for (const t of this.tappeByProblema.get(p) ?? []) {
        const prev = rep.get(t.autore_id);
        if (!prev || t.anno_riferimento < prev.anno_riferimento) rep.set(t.autore_id, t);
      }
      for (const [autoreId, t] of rep) {
        const author = this.authorById.get(autoreId);
        if (author) placed.push({ author, tappa: t, year: author.anno_nascita, x: 0 });
      }
    }
    placed = placed.sort((a, b) => a.year - b.year || a.author.nome.localeCompare(b.author.nome));
    return placed;
  }

  private blurb(node: PlacedNode): string {
    const mb = node.tappa.content.map_blurb;
    if (mb && mb.trim()) return mb.trim();
    const plain = (node.tappa.content.spiegazione ?? '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const dot = plain.indexOf('. ');
    return dot > 40 ? plain.slice(0, dot + 1) : plain.slice(0, 140);
  }

  private sphereColor(a: PhilosopherLocalized): string {
    return philosopherSphereColor(a.anno_nascita);
  }

  // ───────────────────────── render ─────────────────────────

  private render(): void {
    // Marca la modalità sul contenitore → il layout LIM (colonna a piena
    // altezza + caption ancorata) si applica SOLO a "Nel tempo"; "Confronto"
    // resta a scorrimento normale (cerchio + colonne possono essere alti).
    this.element.classList.toggle('cmv-time', this.mode === 'time');
    this.element.classList.toggle('cmv-compare', this.mode === 'compare');

    const concepts = this.presentConcepts();
    const problemi = this.presentProblemi();

    if (concepts.length === 0 && problemi.length === 0) {
      this.element.innerHTML = `<p class="cmv-empty">Nessun contenuto disponibile per la mappa.</p>`;
      return;
    }

    // Normalizza la selezione al primo elemento valido della lente corrente.
    if (this.lens === 'concept') {
      if (!this.selectedId || !concepts.some((c) => c.id === this.selectedId)) {
        this.selectedId = concepts[0]?.id ?? null;
      }
    } else {
      if (!this.selectedId || !problemi.includes(this.selectedId as ProblemaFondamentale)) {
        this.selectedId = problemi[0] ?? null;
      }
    }

    this.element.innerHTML = `
      <header class="cmv-header">
        <div class="cmv-header-text">
          <h1 class="cmv-title">Mappa dei concetti</h1>
          <p class="cmv-subtitle">
            Ogni concetto è un filo che attraversa i secoli. Scegli la lente
            (concetto o problema), poi segui i filosofi nel tempo o mettili a confronto.
          </p>
        </div>
        <div class="cmv-controls">
          <div class="cmv-lens" role="tablist" aria-label="Lente">
            <button type="button" class="cmv-lens-btn${this.lens === 'concept' ? ' is-active' : ''}" data-lens="concept" role="tab" aria-selected="${this.lens === 'concept'}">Concetto</button>
            <button type="button" class="cmv-lens-btn${this.lens === 'problem' ? ' is-active' : ''}" data-lens="problem" role="tab" aria-selected="${this.lens === 'problem'}">Problema</button>
          </div>
          <div class="cmv-mode" role="tablist" aria-label="Modalità">
            <button type="button" class="cmv-mode-btn${this.mode === 'time' ? ' is-active' : ''}" data-mode="time" role="tab" aria-selected="${this.mode === 'time'}">★ Nel tempo</button>
            <button type="button" class="cmv-mode-btn${this.mode === 'compare' ? ' is-active' : ''}" data-mode="compare" role="tab" aria-selected="${this.mode === 'compare'}">● Confronto</button>
          </div>
        </div>
      </header>
      ${this.renderRail(concepts, problemi)}
      ${this.mode === 'time' ? this.renderTime() : this.renderCompare()}
    `;

    this.attachCaptionFit();
  }

  /** (Ri)aggancia l'auto-fit al blurb della caption dopo ogni render. Fuori da
   *  LIM la box non è vincolata → il fit è un no-op (il testo resta al design). */
  private attachCaptionFit(): void {
    this.captionFit?.disconnect();
    this.captionFit = null;
    const blurb = this.element.querySelector<HTMLElement>('.cmv-caption-blurb');
    if (blurb) this.captionFit = fitText(blurb, { min: 13 });
  }

  private renderRail(concepts: ConceptLocalized[], problemi: ProblemaFondamentale[]): string {
    if (this.lens === 'problem') {
      return `<div class="cmv-rail cmv-rail--problem">${problemi
        .map((p) => {
          const on = p === this.selectedId;
          return `<button type="button" class="cmv-rail-btn${on ? ' is-active' : ''}" data-problem="${attr(p)}" style="--pill:${problemTint(p)}">${esc(problemaLabel(p))}</button>`;
        })
        .join('')}</div>`;
    }

    // Lente concetto: concetti raggruppati sotto i 7 problemi (barra = tinta problema).
    const groups = new Map<ProblemaFondamentale, ConceptLocalized[]>();
    for (const c of concepts) {
      const arr = groups.get(c.problema_fondamentale_associato) ?? [];
      arr.push(c);
      groups.set(c.problema_fondamentale_associato, arr);
    }
    const blocks = PROBLEMI_FONDAMENTALI.filter((p) => groups.has(p)).map((p) => {
      const tint = problemTint(p);
      const pills = (groups.get(p) ?? [])
        .map((c) => {
          const on = c.id === this.selectedId;
          return `<button type="button" class="cmv-rail-btn cmv-rail-btn--concept${on ? ' is-active' : ''}" data-concept="${attr(c.id)}" style="--pill:${CATEGORY_COLORS.concept};--group:${tint}">${esc(c.nome)}</button>`;
        })
        .join('');
      return `<div class="cmv-rail-group">
        <span class="cmv-rail-grouplabel"><span class="cmv-rail-groupswatch" style="background:${tint}"></span>${esc(problemaLabel(p))}</span>
        <div class="cmv-rail-pills">${pills}</div>
      </div>`;
    });
    return `<div class="cmv-rail cmv-rail--concept">${blocks.join('')}</div>`;
  }

  // ───────────────────────── ★ NEL TEMPO ─────────────────────────

  private renderTime(): string {
    const col = this.accentColor();
    const placed = this.nodes();
    const N = placed.length;

    if (N === 0) {
      return `<p class="cmv-empty">Nessuna tappa per «${esc(this.focusLabel())}».</p>`;
    }

    if (this.narr.i > N - 1) this.narr.i = N - 1;
    if (this.narr.i < 0) this.narr.i = 0;

    const padX = 64;
    const H = 210;
    const nodeY = 116;
    // Larghezza in PIXEL REALI (mobile-safe): la mappa riempie lo spazio ma NON
    // scende sotto una spaziatura minima leggibile (MIN_GAP). Se non ci sta, la
    // scena CRESCE e si scorre in orizzontale, invece di rimpicciolire le sfere.
    const avail = Math.max(300, (this.element.clientWidth || 1040) - 40);
    const needed = N <= 1 ? avail : 2 * padX + (N - 1) * MIN_GAP;
    const W = Math.max(avail, needed);
    const colW = N <= 1 ? 0 : (W - 2 * padX) / (N - 1);
    placed.forEach((p, i) => {
      p.x = N === 1 ? W / 2 : padX + i * colW;
    });

    const revealed = (i: number) => !this.narr.on || i <= this.narr.i;

    // Fasce dei secoli (gruppi contigui con lo stesso secolo): separatore + etichetta.
    const bandTop = 30;
    const bandBot = 190;
    const floorY = nodeY + NODE_R + 7;
    let bands = '';
    let g = 0;
    while (g < N) {
      const code = centuryCode(placed[g].year);
      let h = g;
      while (h + 1 < N && centuryCode(placed[h + 1].year) === code) h++;
      const left = g === 0 ? 0 : placed[g].x - colW / 2;
      const right = h === N - 1 ? W : placed[h].x + colW / 2;
      const cx = (left + right) / 2;
      bands += `${h === N - 1 ? '' : `<line x1="${right}" y1="${bandTop}" x2="${right}" y2="${bandBot}" stroke="rgba(27,28,43,.12)" stroke-dasharray="2 6"/>`}
        <g class="cmv-band-chip" transform="translate(${cx},${bandTop - 4})">
          <text text-anchor="middle" class="cmv-band-label">${esc(centuryLabel(code))}</text>
        </g>`;
      g = h + 1;
    }

    // Riga di "pavimento" sotto le sfere → àncora visivamente la scena.
    const floor = `<line x1="0" y1="${floorY + 2}" x2="${W}" y2="${floorY + 2}" stroke="url(#cm-floor)" stroke-width="1.5"/>`;

    // Filo del concetto/problema (materico: alone + tratto pieno + luce sopra).
    const vis = placed.filter((_, i) => revealed(i));
    let thread = '';
    if (vis.length > 1) {
      let d = `M${vis[0].x},${nodeY}`;
      for (let k = 1; k < vis.length; k++) {
        const mx = (vis[k - 1].x + vis[k].x) / 2;
        d += ` Q${mx},${nodeY - 12} ${vis[k].x},${nodeY}`;
      }
      thread = `<path d="${d}" fill="none" stroke="${col}" stroke-width="8" stroke-opacity="0.10" stroke-linecap="round"/>
        <path d="${d}" fill="none" stroke="${col}" stroke-width="3" stroke-opacity="0.55" stroke-linecap="round"/>
        <path d="${d}" fill="none" stroke="rgba(255,255,255,0.7)" stroke-width="1" stroke-opacity="0.6" stroke-linecap="round" transform="translate(0,-1.2)"/>`;
    }

    // Archi di trasmissione (influenza tra i filosofi presenti). NIENTE freccia:
    // è una timeline, la trasmissione va sempre passato→futuro (sx→dx).
    const byId = new Map(placed.map((p) => [p.author.id, p]));
    const idx = new Map(placed.map((p, i) => [p.author.id, i]));
    let arrows = '';
    for (const p of placed) {
      for (const tid of p.author.ha_influenzato ?? []) {
        const s = byId.get(p.author.id);
        const t = byId.get(tid);
        if (!s || !t || s === t) continue;
        if (this.narr.on && !(revealed(idx.get(p.author.id)!) && revealed(idx.get(tid)!))) continue;
        const topY = nodeY - 68;
        const mx = (s.x + t.x) / 2;
        arrows += `<path d="M${s.x},${nodeY - 22} Q${mx},${topY} ${t.x},${nodeY - 22}" fill="none"
          stroke="${col}" stroke-width="1.6" stroke-opacity="0.55" stroke-linecap="round"/>`;
      }
    }

    // Nodi filosofo.
    const nodes = placed
      .map((p, i) => {
        const op = revealed(i) ? (this.narr.on && i !== this.narr.i ? 0.45 : 1) : 0;
        const sub = formatYear(p.year);
        return `<g class="cmv-node" data-node-tappa="${attr(p.tappa.id)}" data-node-concept="${attr(p.tappa.concetto_id)}" style="cursor:pointer;opacity:${op};transition:opacity .35s">
        <ellipse cx="${p.x}" cy="${floorY}" rx="${NODE_R * 0.95}" ry="5" fill="rgba(27,28,43,0.20)" filter="url(#cm-ground)"/>
        ${authorSphere(p.x, nodeY, NODE_R, computeInitials(p.author.nome), 16, this.sphereColor(p.author))}
        <text x="${p.x}" y="${nodeY + NODE_R + 18}" text-anchor="middle" class="cmv-node-name">${esc(computeDisplayName(p.author.nome))}</text>
        <text x="${p.x}" y="${nodeY + NODE_R + 33}" text-anchor="middle" class="cmv-node-year">${esc(sub)}</text>
      </g>`;
      })
      .join('');

    const span = N > 1 ? `${formatYear(placed[0].year)} – ${formatYear(placed[N - 1].year)}` : '—';
    const kindLabel = this.lens === 'concept' ? 'CONCETTO' : 'PROBLEMA';
    const head = `<div class="cmv-time-head">
      <span class="cmv-time-title" style="color:${col}">${esc(this.focusLabel())}</span>
      <span class="cmv-time-sub">${kindLabel} · ${N} · ${esc(span)}</span>
    </div>`;

    // Toolbar "Racconta" + caption.
    const tlbar = `<div class="cmv-tlbar">
      ${
        this.narr.on
          ? `
        <button type="button" class="cmv-tlbar-btn cmv-tlbar-nav" data-narr="prev">◀ Indietro</button>
        <span class="cmv-tlbar-step">Passo ${this.narr.i + 1}/${N}</span>
        <button type="button" class="cmv-tlbar-btn cmv-tlbar-nav" data-narr="next">${this.narr.i < N - 1 ? 'Avanti ▶' : 'Fine'}</button>
        <button type="button" class="cmv-tlbar-btn cmv-tlbar-exit" data-narr="toggle">Chiudi ✕</button>
      `
          : `<button type="button" class="cmv-tlbar-btn primary" data-narr="toggle">▶ Racconta</button>`
      }
    </div>`;
    const cur = placed[this.narr.i];
    const caption =
      this.narr.on && cur
        ? `<div class="cmv-caption" style="border-color:${col}">
      <span class="cmv-caption-who">${esc(cur.author.nome)}</span>
      <span class="cmv-caption-yr">${esc(formatYear(cur.year))}</span>
      <div class="cmv-caption-blurb">${esc(this.blurb(cur))}</div></div>`
        : '';

    return `${head}${tlbar}
      <div class="cmv-canvas cmv-canvas--time">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" style="width:${W}px;max-width:none"
             role="img" aria-label="${attr(this.focusLabel())}">
          <defs>${authorSphereDefs(placed.map((p) => this.sphereColor(p.author)))}
            <linearGradient id="cm-floor" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stop-color="rgba(27,28,43,0)"/>
              <stop offset="0.5" stop-color="rgba(27,28,43,0.14)"/>
              <stop offset="1" stop-color="rgba(27,28,43,0)"/>
            </linearGradient>
            <filter id="cm-ground" x="-60%" y="-120%" width="220%" height="340%">
              <feGaussianBlur stdDeviation="2.4"/>
            </filter>
          </defs>
          <g>${bands}</g>${floor}${thread}<g>${arrows}</g><g>${nodes}</g>
        </svg>
      </div>${caption}`;
  }

  // ───────────────────────── ● CONFRONTO ─────────────────────────

  private renderCompare(): string {
    const col = this.accentColor();
    const placed = this.nodes();

    if (placed.length === 0) {
      return `<p class="cmv-empty">Nessuna tappa per «${esc(this.focusLabel())}».</p>`;
    }

    // Cerchio = SELETTORE. Raggio e tela SCALANO col numero di filosofi: con
    // molti nodi (es. un problema con 30+) il cerchio si allarga così sfere e
    // nomi NON si accavallano (prima era fisso R=118 → tutto appiccicato).
    const N = placed.length;
    const names = placed.map((p) => computeDisplayName(p.author.nome));
    const maxLabelW = Math.max(60, ...names.map((n) => n.length * 7.2));
    // Arco minimo per nodo = sfera + aria; da lì il raggio (mai sotto 118).
    const R = Math.max(118, (N * (2 * NODE_R + 12)) / (2 * Math.PI));
    // Tela = diametro sfere + etichette che si aprono a RAGGIERA verso l'esterno.
    const W = Math.round(2 * (R + NODE_R) + 2 * maxLabelW + 48);
    const H = Math.round(2 * (R + NODE_R) + 128);
    const cx = W / 2;
    const cy = H / 2;
    const nodeByAuthor = new Map(placed.map((p) => [p.author.id, p]));
    let edges = '';
    let circ = '';
    placed.forEach((p, i) => {
      const ang = -Math.PI / 2 + (i / N) * Math.PI * 2;
      const x = cx + Math.cos(ang) * R;
      const y = cy + Math.sin(ang) * R;
      const sel = this.compare.has(p.author.id);
      const dim = this.compare.size > 0 && !sel;
      // Etichetta RADIALE (verso l'esterno): i nomi si aprono a raggiera invece
      // di impilarsi sotto le sfere → niente sovrapposizioni anche con molti nodi.
      const lx = cx + Math.cos(ang) * (R + NODE_R + 9);
      const ly = cy + Math.sin(ang) * (R + NODE_R + 9);
      const ca = Math.cos(ang);
      const anchor = ca > 0.25 ? 'start' : ca < -0.25 ? 'end' : 'middle';
      const dyEm = Math.sin(ang) > 0.5 ? '0.92em' : Math.sin(ang) < -0.5 ? '-0.35em' : '0.32em';
      edges += `<line x1="${cx}" y1="${cy}" x2="${x}" y2="${y}" stroke="${col}" stroke-width="2" stroke-opacity="${dim ? 0.1 : 0.32}"/>`;
      circ += `<g class="cmv-node" data-cmp-id="${attr(p.author.id)}" style="cursor:pointer;opacity:${dim ? 0.4 : 1}">
        ${sel ? `<circle cx="${x}" cy="${y}" r="${NODE_R + 8}" fill="none" stroke="${CATEGORY_COLORS.philosopher}" stroke-width="3.5"/>` : ''}
        ${authorSphere(x, y, NODE_R, computeInitials(p.author.nome), 16, this.sphereColor(p.author))}
        <text x="${lx}" y="${ly}" dy="${dyEm}" text-anchor="${anchor}" class="cmv-node-name">${esc(names[i])}</text>
      </g>`;
    });

    const label = this.focusLabel();
    const hubW = Math.max(140, label.length * 11 + 44);
    const kindLabel = this.lens === 'concept' ? 'CONCETTO' : 'PROBLEMA';
    const hub = `<rect x="${cx - hubW / 2}" y="${cy - 28}" width="${hubW}" height="56" rx="28" fill="${col}" filter="url(#auth-sh)"/>
      <text x="${cx}" y="${cy - 3}" text-anchor="middle" class="cmv-hub-title">${esc(label)}</text>
      <text x="${cx}" y="${cy + 16}" text-anchor="middle" class="cmv-hub-sub">${kindLabel} · ${placed.length}</text>`;

    // Colonne di confronto: la spiegazione della tappa affiancata — il confronto è QUI.
    let cols: string;
    if (this.compare.size === 0) {
      cols = `<p class="cmv-cmp-instr">Scegli 2-3 filosofi nel cerchio per confrontare il loro trattamento di «${esc(label)}».</p>`;
    } else {
      cols = `<div class="cmv-cmp-cols">${[...this.compare]
        .map((id) => {
          const node = nodeByAuthor.get(id);
          if (!node) return '';
          const cit = node.tappa.content.citazioni[0];
          const meta = cit ? node.tappa.citazioni.find((m) => m.id === cit.id) : undefined;
          const citHtml = cit
            ? `<figure class="cmv-cmp-cit">
                <blockquote lang="${attr(meta?.lingua_originale ?? 'la')}">${esc(cit.testo_tradotto)}</blockquote>
              </figure>`
            : '';
          return `<article class="cmv-cmp-col">
          <header class="cmv-cmp-col-head">
            <span class="cmv-cmp-col-name">${esc(node.author.nome)}</span>
            <button type="button" class="cmv-cmp-rm" data-rm="${attr(id)}" aria-label="rimuovi">×</button>
          </header>
          <div class="cmv-cmp-col-blurb" style="border-color:${col}">${esc(this.blurb(node))}</div>
          <div class="cmv-cmp-col-text">${node.tappa.content.spiegazione ?? ''}</div>
          ${citHtml}
          <button type="button" class="cmv-cmp-col-open" data-open-tappa="${attr(node.tappa.id)}" data-open-concept="${attr(node.tappa.concetto_id)}">Apri tappa →</button>
        </article>`;
        })
        .join('')}</div>`;
    }

    return `<div class="cmv-canvas cmv-compare-stage">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="${attr(label)}">
          <defs>${authorSphereDefs(placed.map((p) => this.sphereColor(p.author)))}</defs>
          <g>${edges}</g>${hub}<g>${circ}</g>
        </svg>
      </div>${cols}`;
  }

  // ───────────────────────── eventi ─────────────────────────

  private handleClick(ev: Event): void {
    const target = ev.target as Element;

    const lensBtn = target.closest<HTMLElement>('[data-lens]');
    if (lensBtn) {
      const next = lensBtn.dataset.lens as Lens;
      if (next !== this.lens) {
        this.lens = next;
        this.selectedId = null; // riselezionato al primo valido in render()
        this.narr = { on: false, i: 0 };
        this.compare = new Set();
        this.render();
      }
      return;
    }

    const modeBtn = target.closest<HTMLElement>('[data-mode]');
    if (modeBtn) {
      this.mode = modeBtn.dataset.mode as Mode;
      this.render();
      return;
    }

    const conceptBtn = target.closest<HTMLElement>('[data-concept]');
    if (conceptBtn) {
      this.selectedId = conceptBtn.dataset.concept!;
      this.narr = { on: false, i: 0 };
      this.compare = new Set();
      this.render();
      return;
    }

    const problemBtn = target.closest<HTMLElement>('[data-problem]');
    if (problemBtn) {
      this.selectedId = problemBtn.dataset.problem!;
      this.narr = { on: false, i: 0 };
      this.compare = new Set();
      this.render();
      return;
    }

    const narrBtn = target.closest<HTMLElement>('[data-narr]');
    if (narrBtn) {
      const N = this.nodes().length;
      const act = narrBtn.dataset.narr;
      if (act === 'toggle') this.narr = { on: !this.narr.on, i: 0 };
      else if (act === 'prev') this.narr.i = Math.max(0, this.narr.i - 1);
      else if (act === 'next') {
        if (this.narr.i < N - 1) this.narr.i++;
        else this.narr.on = false;
      }
      this.render();
      return;
    }

    const rm = target.closest<HTMLElement>('[data-rm]');
    if (rm) {
      this.compare.delete(rm.dataset.rm!);
      this.render();
      return;
    }

    const open = target.closest<HTMLElement>('[data-open-tappa]');
    if (open) {
      this.context.bus.emit('navigate:tappa', {
        id: open.dataset.openTappa,
        conceptId: open.dataset.openConcept,
      });
      return;
    }

    const cmp = target.closest<HTMLElement>('[data-cmp-id]');
    if (cmp) {
      const id = cmp.dataset.cmpId!;
      let added = false;
      if (this.compare.has(id)) this.compare.delete(id);
      else if (this.compare.size >= 3) return;
      else { this.compare.add(id); added = true; }
      this.render();
      // Scegliendo un filosofo la sua colonna appare SOTTO il cerchio: portala
      // in vista così non serve scorrere a mano per vedere il confronto.
      if (added) {
        requestAnimationFrame(() => {
          this.element.querySelector('.cmv-cmp-cols')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
      return;
    }

    const node = target.closest<HTMLElement>('[data-node-tappa]');
    if (node) {
      this.context.bus.emit('navigate:tappa', {
        id: node.dataset.nodeTappa,
        conceptId: node.dataset.nodeConcept,
      });
      return;
    }
  }
}

// ───────────────────────── helper puri ─────────────────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function attr(s: string): string {
  return esc(s).replace(/"/g, '&quot;');
}

/** Iniziali "umane" per il fallback quando il ritratto non carica. */
function computeInitials(name: string): string {
  const caps = name.match(/\p{Lu}[\p{L}']*/gu) ?? [];
  if (caps.length >= 2) return (caps[0]!.charAt(0) + caps[1]!.charAt(0)).toUpperCase();
  if (caps.length === 1) return caps[0]!.slice(0, 2).toUpperCase();
  const letters = (name.match(/\p{L}/gu) ?? []).slice(0, 2).join('');
  return letters.toUpperCase() || '?';
}

/**
 * Codice-secolo di un anno (gestisce a.C. per anni negativi). Es. -470 → "bc-5",
 * 1724 → "ad-18". L'anno 0 non esiste nel calendario storico e non ricorre.
 */
function centuryCode(year: number): string {
  if (year < 0) return 'bc-' + (Math.floor((Math.abs(year) - 1) / 100) + 1);
  return 'ad-' + (Math.floor((year - 1) / 100) + 1);
}
/** Etichetta leggibile del secolo: "V sec. a.C." / "XVII sec.". */
function centuryLabel(code: string): string {
  const [era, n] = code.split('-');
  const roman = toRoman(Number(n));
  return era === 'bc' ? `${roman} sec. a.C.` : `${roman} sec.`;
}

const ROMAN_UNITS = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'];
const ROMAN_TENS = ['', 'X', 'XX', 'XXX', 'XL'];
/** Numeri romani per i secoli plausibili (1..49 copre a.C. e d.C. del catalogo). */
function toRoman(n: number): string {
  if (n <= 0 || n > 49) return String(n);
  return (ROMAN_TENS[Math.floor(n / 10)] ?? '') + ROMAN_UNITS[n % 10];
}

/**
 * Sette declinazioni calde del token `problem` (ambra), una per problema
 * fondamentale, ancorate all'hex del design system (nessun colore hardcoded a
 * mano): l'ambra base viene schiarita/scurita lungo l'ordine ministeriale, così
 * i 7 problemi restano una famiglia riconoscibile ma distinguibili come barra.
 */
function problemTint(p: ProblemaFondamentale): string {
  const i = PROBLEMI_FONDAMENTALI.indexOf(p);
  const base = CATEGORY_COLORS.problem;
  const t = i < 0 ? 0 : i / (PROBLEMI_FONDAMENTALI.length - 1); // 0..1
  const amt = (t - 0.5) * 0.7; // -0.35 (chiaro) .. +0.35 (scuro)
  return amt < 0 ? lighten(base, -amt) : darken(base, amt);
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mix(a: [number, number, number], b: [number, number, number], t: number): string {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}
const lighten = (hex: string, t: number) => mix(hexToRgb(hex), [255, 255, 255], t);
const darken = (hex: string, t: number) => mix(hexToRgb(hex), [0, 0, 0], t);
